import { QueryClientProvider, useQuery } from '@tanstack/react-query'
import React from 'react'
import { BrowserRouter, Link as RouterLink, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom'
import { AlertTriangle, Building2, CreditCard, MessageCircle } from 'lucide-react'
import './App.css'
import logo from './assets/logo.png'
import { AuthProvider, RequireCapability, RequireHealthAnalysisAccess, RequireInsuranceNewsAccess, RequirePermission, RequireRole, RequireVisibleMenuKey, useAuth } from './auth/AuthContext'
import { Capabilities, Permissions, Roles } from './auth/rbac'
import { useNotifications } from './ui/NotificationCenter'
import { absoluteApiFilePreviewUrl, api, resolveApiAssetUrl } from './lib/api'
import { formatApiDateTimeDisplay } from './lib/date'
import { openNotificationHref, resolveNotificationHref } from './lib/notificationRouting'
import { queryClient } from './lib/queryClient'
import WebPageviewCollector from './analytics/WebPageviewCollector'
import AvailabilityAdminPage from './pages/AvailabilityAdmin'
import CounterpartDirectoryPage from './pages/CounterpartDirectory'
import DashboardPage from './pages/Dashboard'
import MenuOverviewPage from './pages/MenuOverviewPage'
import FilesPage from './pages/Files'
import OpsKakaoTalkManagementPage from './pages/OpsKakaoTalkManagement'
import OpsLegalPolicyManagementPage from './pages/ops/OpsLegalPolicyManagement'
import OpsSupportGuideManualsPage from './pages/OpsSupportGuideManualsPage'
import OpsApiTestPage from './pages/OpsApiTestPage'
import OpsCustomerWorkHubManagementPage from './pages/OpsCustomerWorkHubManagementPage'
import OpsOtherManagementPage from './pages/OpsOtherManagementPage'
import OpsCounselPracticeManagementPage from './pages/OpsCounselPracticeManagementPage'
import AuditLogsPage from './pages/AuditLogs'
import OpsHistoryManagementPage from './pages/OpsHistoryManagement'
import OpsUsageDashboardPage from './pages/OpsUsageDashboardPage'
import OpsOrganizationUserManagementPage from './pages/OpsOrganizationUserManagementPage'
import OpsOperationalCostsPage from './pages/OpsOperationalCostsPage'
import OperatorAlimtalkSenderPage from './pages/OperatorAlimtalkSender'
import PermissionsAdminPage from './pages/PermissionsAdmin'
import ForbiddenPage from './pages/Forbidden'
import UnauthorizedPage from './pages/Unauthorized'
import NotFoundPage from './pages/NotFound'
import BadRequestPage from './pages/BadRequest'
import InternalServerErrorPage from './pages/InternalServerError'
import BadGatewayPage from './pages/BadGateway'
import ServiceUnavailablePage from './pages/ServiceUnavailable'
import GatewayTimeoutPage from './pages/GatewayTimeout'
// 초대(Invites) 기능은 오픈베타에서는 비활성화한다. (필요 시 추후 재도입)
import LoginPage from './pages/Login'
import SignupPage from './pages/Signup'
import TermsPage from './pages/Terms'
import PrivacyPage from './pages/Privacy'
import ResetPasswordPage from './pages/ResetPassword'
import FindIdPage from './pages/FindId'
import ForgotPasswordPage from './pages/ForgotPassword'
import NaverCallbackPage from './pages/NaverCallback'
import AuthBridgePage from './pages/AuthBridge'
import PublicCardPage from './pages/PublicCard'
import PublicLandingPage from './pages/PublicLanding'
import SgezLandingPage from './pages/SgezLanding'
import AboutPage from './pages/public/AboutPage'
import B2bPartnershipPage from './pages/public/B2bPartnershipPage'
import ProductPage from './pages/public/ProductPage'
import PricingPage from './pages/public/PricingPage'
import CheckoutPage from './pages/public/CheckoutPage'
import RefundPolicyPage from './pages/public/RefundPolicyPage'
import SupportHubPage from './pages/public/SupportHubPage'
import SupportNoticesPage from './pages/public/SupportNoticesPage'
import SupportQnaPage from './pages/public/SupportQnaPage'
import SupportFaqPage from './pages/public/SupportFaqPage'
import SupportGuidePage from './pages/public/SupportGuidePage'
import PublicInquiryPage from './pages/public/PublicInquiryPage'
import MyPublicCardPage from './pages/MyPublicCard'
import MyPublicLandingPage from './pages/MyPublicLanding'
import SalesOrgHomepagePage from './pages/SalesOrgHomepage'
import CategoryLandingPagesPage from './pages/CategoryLandingPages'
import CategoryLandingPageViewPage from './pages/CategoryLandingPageView'
import CategoryLandingPageEditPage from './pages/CategoryLandingPageEdit'
import CategoryLandingPagePreview from './pages/CategoryLandingPagePreview'
import NoticeDetailPage from './pages/NoticeDetail'
import NoticeEditPage from './pages/NoticeEdit'
import NoticeLogsPage from './pages/NoticeLogs'
import NoticeNewPage from './pages/NoticeNew'
import NoticesPage from './pages/Notices'
import NoticeTemplatesPage from './pages/NoticeTemplates'
import HomepageTemplatesPage from './pages/HomepageTemplatesPage'
import OperatorNotificationTemplatesPage from './pages/OperatorNotificationTemplates'
import SurveyTemplatesPage from './pages/SurveyTemplates'
import MessageTemplatesPage from './pages/MessageTemplates'
import CustomerMemoTemplatesPage from './pages/CustomerMemoTemplatesPage'
import OrgsPage from './pages/Orgs'
import OrgDashboardPage from './pages/OrgDashboardPage'
import OrgLibraryPage from './pages/OrgLibraryPage'
import ProfilePage from './pages/Profile'
import ReportsPage from './pages/Reports'
import TermsLibraryPage from './pages/TermsLibrary'
import HealthAnalysisPage from './pages/HealthAnalysis'
import HiddenInsuranceMoneyPage from './pages/HiddenInsuranceMoneyPage'
import HiddenInsuranceMoneyLandingPage from './pages/HiddenInsuranceMoneyLandingPage'
import HealthAuthPage from './pages/HealthAuth'
import CustomersPage from './pages/Customers'
import SupportInquiriesPage from './pages/SupportInquiries'
import SupportInquiryDetailPage from './pages/SupportInquiryDetail'
import AccountDeletionPage from './pages/AccountDeletion'
import EventsPage from './pages/EventsPage'
import VoteBoardPage from './pages/VoteBoardPage'
import SalesImagesListPage from './pages/SalesImagesList'
import SalesImagesOrgTemplatesPage from './pages/SalesImagesOrgTemplatesPage'
import SalesImagesCreatePage from './pages/SalesImagesCreate'
import SalesImagesEditPage from './pages/SalesImagesEdit'
import FireBuildingRegistryPage from './pages/FireBuildingRegistry'
import DesignSupportHubPage from './pages/designSupport/DesignSupportHubPage'
import DesignSupportDiseaseCodePage from './pages/DesignSupportDiseaseCodePage'
import DesignSupportSurgerySearchPage from './pages/DesignSupportSurgerySearchPage'
import DesignSupportMajorDiseaseNPage from './pages/DesignSupportMajorDiseaseNPage'
import DesignSupportDataAdminPage from './pages/DesignSupportDataAdminPage'
import RawDataAdminPage from './pages/RawDataAdminPage'
import PublicDataAdminPage from './pages/PublicDataAdminPage'
import OpsTermsManagementPage from './pages/OpsTermsManagementPage'
import RequestDetailPage from './pages/RequestDetail'
import RequestNewPage from './pages/RequestNew'
import RequestSharePage from './pages/RequestShare'
import SignClaimSignaturePage from './pages/SignClaimSignature'
import SignAfterContractCertificatePage from './pages/SignAfterContractCertificate'
import RequestsPage from './pages/Requests'
import SubscriptionsPage from './pages/Subscriptions'
import BillingStatisticsPage from './pages/BillingStatisticsPage'
import BillingConfigPage from './pages/BillingConfigPage'
import BillingOrgManagementPage from './pages/BillingOrgManagementPage'
import UserBillingPage from './pages/UserBillingPage'
import BillingSubscribePage from './pages/BillingSubscribePage'
import Uw3nnExceptionRulesAdminPage from './pages/Uw3nnExceptionRulesAdmin'
import Uw3nnExceptionsLookupPage from './pages/Uw3nnExceptionsLookup'
import InsurersMasterAdminPage from './pages/InsurersMasterAdmin'
import UserDetailPage from './pages/UserDetail'
import UsersPage from './pages/Users'
import SignupRequestsPage from './pages/SignupRequests'
import SignupRequestDetailPage from './pages/SignupRequestDetail'
import WithdrawalApprovalRequestsPage from './pages/WithdrawalApprovalRequests'
import KakaoBulkSenderPage from './pages/KakaoBulkSender'
import ClaimsPage from './pages/Claims'
import ClaimApplicationsPage from './pages/ClaimApplications'
import ClaimApplicationDetailPage from './pages/ClaimApplicationDetail'
import ClaimFormsManagementPage from './pages/ClaimFormsManagementPage'
import AfterContractCertificateTemplatesPage from './pages/AfterContractCertificateTemplates'
import PolicyDeliveryBookTemplatesPage from './pages/PolicyDeliveryBookTemplates'
import CoverageRuleTemplatesPage from './pages/CoverageRuleTemplatesPage'
import ReportTemplatesPage from './pages/ReportTemplatesPage'
import OpsStatsPage from './pages/OpsStatsPage'
import ClaimIntakePublicPage from './pages/ClaimIntakePublic'
import InsurerSystemsPage from './pages/InsurerSystems'
import InsurerBusinessHubPage from './pages/InsurerBusinessHubPage'
import CalendarPage from './pages/CalendarPage'
import PersonalTaskBoardPage from './pages/PersonalTaskBoardPage'
import EducationCenterPage from './pages/EducationCenterPage'
import SurveyManagementPage from './pages/SurveyManagement'
import SurveyCatalogTemplateEditPage from './pages/SurveyCatalogTemplateEditPage'
import SurveyCreationPage from './pages/SurveyCreationPage'
import SurveyMyTemplateEditPage from './pages/SurveyMyTemplateEditPage'
import CustomerManagementPage from './pages/CustomerManagementPage'
import CustomerMaturityLedgerPage from './pages/CustomerMaturityLedgerPage'
import CustomerWorkQueuePage from './pages/CustomerWorkHubPage'
import CustomerMemoSearchPage from './pages/CustomerMemoSearchPage'
import CustomerMemoAppEditorPage from './pages/CustomerMemoAppEditorPage'
import CustomerRegistrationWizardPage from './pages/CustomerRegistrationWizardPage'
import CustomerDetailPage from './pages/CustomerDetailPage'
import CoverageRemodelingSimulationPage from './pages/CoverageRemodelingSimulationPage'
import MedicalExpenseReimbursementCalculatorPage from './pages/MedicalExpenseReimbursementCalculator'
import WorkSupportToolsPage from './pages/WorkSupportTools'
import CounselPracticePage from './pages/CounselPracticePage'
import CounselSurveySessionRunPage from './pages/CounselSurveySessionRun'
import PublicCounselSurveyPage from './pages/PublicCounselSurvey'
import PolicyLookupPage from './pages/PolicyLookup'
import MyConsultationsPage from './pages/MyConsultations'
import InsuranceMarketInsightsPage from './pages/InsuranceMarketInsightsPage'
import InsuranceNewsPage from './pages/InsuranceNews'
import InsuranceNewsNewPage from './pages/InsuranceNewsNew'
import InsuranceNewsEditPage from './pages/InsuranceNewsEdit'
import InsuranceNewsDailyDigestNewPage from './pages/InsuranceNewsDailyDigestNew'
import InsuranceNewsDailyDigestEditPage from './pages/InsuranceNewsDailyDigestEdit'
import InsurerNewslettersPage from './pages/InsurerNewsletters'
import SystemNoticesPage from './pages/SystemNotices'
import SystemNoticeNewPage from './pages/SystemNoticeNew'
import SystemNoticeEditPage from './pages/SystemNoticeEdit'
import SystemNoticeDetailPage from './pages/SystemNoticeDetail'
import BannerNewPage from './pages/BannerNew'
import BannerEditPage from './pages/BannerEdit'
import OrgBannersPage from './pages/OrgBanners'
import OrgBannerNewPage from './pages/OrgBannerNew'
import OrgBannerEditPage from './pages/OrgBannerEdit'
import OrgLandingPage from './pages/OrgLandingPage'
import OrgLandingPageEditPage from './pages/OrgLandingPageEdit'
import { ErrorBoundary } from './ui/ErrorBoundary'
import { NotificationProvider } from './ui/NotificationCenter'
import { ToastProvider } from './ui/ToastProvider'
import SanitizedRichText from './ui/SanitizedRichText'
import { DialogBackdrop, DialogPanel } from './ui/DialogLayout'
import GlobalToastBridge from './ui/GlobalToastBridge'
import { ENABLE_BILLING_COUPONS, ENABLE_BILLING_POINTS, ENABLE_USER_BILLING } from './config/featureFlags'
import BillingPaywallShellNudge from './ui/BillingPaywallShellNudge'
import ShellSidebar from './components/ShellSidebar'
import HeaderMenuFavorites from './components/HeaderMenuFavorites'
import { resolveShellLayoutTier, SHELL_LAYOUT_TIERS, shellMainContentClass } from './lib/shellLayout'
import { EVENTS_PATH, KAKAO_OFFICIAL_CHANNEL_URL, SUPPORT_GUIDE_PATH, SUPPORT_INQUIRIES_PATH, VOTE_BOARD_PATH } from './config/shellHeader'

const BILLING_SUBSCRIBE_PATH = '/billing/subscribe'
const ORG_MEMBERSHIP_REQUEST_PATH = '/profile?orgMembership=1'

const Link = React.forwardRef(function AppLink({ to, ...props }, ref) {
  delete props.title
  return <RouterLink ref={ref} to={to} {...props} />
})

function isBillingAccessAllowedPath(pathname) {
  if (!pathname) return false
  return pathname === '/billing'
    || pathname.startsWith('/billing/')
    || pathname === '/profile'
    || pathname === SUPPORT_INQUIRIES_PATH
    || pathname.startsWith(`${SUPPORT_INQUIRIES_PATH}/`)
}

function BillingAccessLoadingPage() {
  return (
    <div className="mx-auto flex min-h-[18rem] max-w-2xl items-center justify-center text-sm text-slate-600">
      서비스 이용 상태를 확인하고 있습니다.
    </div>
  )
}

function BillingAccessRequiredPage({ billingStatusPath }) {
  return (
    <section className="flex min-h-[calc(100vh-14rem)] items-center justify-center">
      <div className="w-full max-w-3xl rounded-lg border border-slate-200 bg-white p-8 shadow-sm md:p-10">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-start">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-700 ring-1 ring-amber-200">
            <CreditCard className="h-7 w-7" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-slate-950">서비스 이용 준비가 필요합니다</h1>
            <p className="mt-3 text-base leading-7 text-slate-700">
              현재 사용 가능한 구독 정보가 없습니다.
              <br />
              서비스 이용을 위해 구독을 시작하거나 기존 구독 상태를 확인해 주세요.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to={BILLING_SUBSCRIBE_PATH}
                className="inline-flex items-center gap-2 rounded bg-brand-navy px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90"
              >
                <CreditCard className="h-4 w-4" aria-hidden="true" />
                구독하러가기
              </Link>
              {billingStatusPath ? (
                <Link
                  to={billingStatusPath}
                  className="inline-flex items-center gap-2 rounded border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50"
                >
                  <CreditCard className="h-4 w-4" aria-hidden="true" />
                  구독 확인
                </Link>
              ) : null}
              <Link
                to={ORG_MEMBERSHIP_REQUEST_PATH}
                className="inline-flex items-center gap-2 rounded border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-50"
              >
                <Building2 className="h-4 w-4" aria-hidden="true" />
                계약조직 이동 신청
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

function BillingAccessErrorPage() {
  return (
    <section className="mx-auto max-w-2xl rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <div className="flex items-start gap-4">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-700 ring-1 ring-red-200">
          <AlertTriangle className="h-5 w-5" aria-hidden="true" />
        </div>
        <div className="min-w-0">
          <h1 className="text-lg font-semibold text-slate-950">구독 정보를 확인하지 못했습니다</h1>
          <p className="mt-2 text-sm leading-6 text-slate-700">
            잠시 후 다시 시도하거나 고객문의로 요청해 주세요. 로그아웃은 좌측 메뉴에서 진행할 수 있습니다.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <Link
              to={SUPPORT_INQUIRIES_PATH}
              className="inline-flex items-center gap-2 rounded bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
            >
              <MessageCircle className="h-4 w-4" aria-hidden="true" />
              고객문의
            </Link>
          </div>
        </div>
      </div>
    </section>
  )
}

function resolveShellNavLogoSrc(fileId) {
  if (!fileId) return null
  const raw = String(fileId).trim()
  if (!raw) return null
  if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('/')) {
    return resolveApiAssetUrl(raw)
  }
  return absoluteApiFilePreviewUrl(raw)
}

function RequireAuth({ children }) {
  const { isAuthenticated, initializing } = useAuth()
  const location = useLocation()
  if (initializing) {
    return <div />
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />
  }
  return children
}

function LegacyAfterContractCertificateRedirect() {
  const location = useLocation()
  return <Navigate to={{ pathname: '/work/tools', search: location.search }} replace />
}

/** 알림·카카오 OG용 `/share/claim-signature/:token` → 실제 서명 화면 `/sign/claim-signature/:token` */
function ShareClaimSignatureRedirect() {
  const { token } = useParams()
  if (!token) return <Navigate to="/" replace />
  return <Navigate to={`/sign/claim-signature/${encodeURIComponent(token)}`} replace />
}

/** 알림·OG용 `/share/claim-intake/:token` → 입력 화면 `/claims/intake/:token` */
function ShareClaimIntakeRedirect() {
  const { token } = useParams()
  if (!token) return <Navigate to="/" replace />
  return <Navigate to={`/claims/intake/${encodeURIComponent(token)}`} replace />
}

function getPublicSurveyTokenFromSearch(search) {
  try {
    return new URLSearchParams(search || '').get('publicSurveyToken') || ''
  } catch {
    return ''
  }
}

function RootRoute() {
  const location = useLocation()
  const publicSurveyToken = getPublicSurveyTokenFromSearch(location.search).trim()
  if (publicSurveyToken) {
    return <PublicCounselSurveyPage tokenOverride={publicSurveyToken} />
  }
  return (
    <RequireAuth>
      <Shell>
        <DashboardPage />
      </Shell>
    </RequireAuth>
  )
}

function Shell({ children }) {
  const { user, logout, hasAnyRole, hasAnyPermission, hasAnyCapability } = useAuth()
  const shellNavLogoSrc = resolveShellNavLogoSrc(user?.shellNavOrgLogoFileId)
  const shellBrandedNav = Boolean(shellNavLogoSrc)
  const { badgeCount, notifications, markAllRead, markRead, dismiss, clear } = useNotifications()
  const navigate = useNavigate()
  const [menuOpen, setMenuOpen] = React.useState(false)
  const [notifOpen, setNotifOpen] = React.useState(false)
  const [popupNotice, setPopupNotice] = React.useState(null)
  const [readNoticeIds, setReadNoticeIds] = React.useState(() => {
    try {
      const stored = localStorage.getItem('systemNoticeReadIds')
      return stored ? JSON.parse(stored) : []
    } catch {
      return []
    }
  })
  // 오늘 그만 보기: 날짜별로 공지 ID 배열 저장 { "2026-02-26": [1, 2, 3] }
  const [dismissedToday, setDismissedToday] = React.useState(() => {
    try {
      const stored = localStorage.getItem('systemNoticeDismissedToday')
      return stored ? JSON.parse(stored) : {}
    } catch {
      return {}
    }
  })

  // 오늘 날짜 문자열 (YYYY-MM-DD)
  const getTodayString = () => {
    const today = new Date()
    const year = today.getFullYear()
    const month = String(today.getMonth() + 1).padStart(2, '0')
    const day = String(today.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  // 오늘 그만 보기로 설정된 공지인지 확인
  const isDismissedToday = (noticeId) => {
    const today = getTodayString()
    const todayDismissed = dismissedToday[today] || []
    return todayDismissed.includes(noticeId)
  }

  // 오늘 그만 보기로 설정
  const handleDismissToday = (noticeId) => {
    const today = getTodayString()
    const todayDismissed = dismissedToday[today] || []
    if (!todayDismissed.includes(noticeId)) {
      const newDismissed = {
        ...dismissedToday,
        [today]: [...todayDismissed, noticeId]
      }
      setDismissedToday(newDismissed)
      localStorage.setItem('systemNoticeDismissedToday', JSON.stringify(newDismissed))
    }
    setPopupNotice(null)
    // 다음 공지가 있으면 표시
    if (popupNoticesQuery.data && popupNoticesQuery.data.length > 0) {
      const unreadNotices = popupNoticesQuery.data.filter(notice => 
        ![...readNoticeIds, noticeId].includes(notice.id) && !isDismissedToday(notice.id)
      )
      if (unreadNotices.length > 0) {
        const sorted = [...unreadNotices].sort((a, b) => (b.priority || 0) - (a.priority || 0))
        setPopupNotice(sorted[0])
      }
    }
  }
  const notifRef = React.useRef(null)

  React.useEffect(() => {
    function onDocDown(e) {
      const t = e?.target
      if (notifOpen) {
        const el = notifRef.current
        if (el && !el.contains(t)) setNotifOpen(false)
      }
    }
    function onEsc(e) {
      if (e.key === 'Escape') setNotifOpen(false)
    }
    document.addEventListener('mousedown', onDocDown)
    window.addEventListener('keydown', onEsc)
    return () => {
      document.removeEventListener('mousedown', onDocDown)
      window.removeEventListener('keydown', onEsc)
    }
  }, [notifOpen])

  // 팝업 공지 조회
  const popupNoticesQuery = useQuery({
    queryKey: ['system-notices-popup'],
    queryFn: async () => {
      const res = await api.get('/system-notices/popup')
      return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
    },
    enabled: !!user, // 로그인한 사용자만
    refetchOnWindowFocus: false,
    staleTime: 5 * 60 * 1000, // 5분간 캐시
  })

  // 팝업 공지 표시
  React.useEffect(() => {
    if (!popupNoticesQuery.data || popupNoticesQuery.data.length === 0) return
    if (!popupNotice) {
      // 읽지 않은 공지 중 오늘 그만 보기로 설정되지 않은 것만 필터링
      const unreadNotices = popupNoticesQuery.data.filter(notice => 
        !readNoticeIds.includes(notice.id) && !isDismissedToday(notice.id)
      )
      if (unreadNotices.length > 0) {
        // priority 내림차순 정렬 (이미 백엔드에서 정렬되어 있지만 안전을 위해)
        const sorted = [...unreadNotices].sort((a, b) => (b.priority || 0) - (a.priority || 0))
        setPopupNotice(sorted[0])
      }
    }
  }, [popupNoticesQuery.data, readNoticeIds, popupNotice, dismissedToday])

  // 팝업 닫기 및 읽음 처리
  const handleClosePopup = async (noticeId) => {
    try {
      await api.post(`/system-notices/${noticeId}/read`)
      const newReadIds = [...readNoticeIds, noticeId]
      setReadNoticeIds(newReadIds)
      localStorage.setItem('systemNoticeReadIds', JSON.stringify(newReadIds))
    } catch (err) {
      console.error('Failed to mark notice as read:', err)
    }
    setPopupNotice(null)
    // 다음 공지가 있으면 표시
    if (popupNoticesQuery.data && popupNoticesQuery.data.length > 0) {
      const unreadNotices = popupNoticesQuery.data.filter(notice => 
        ![...readNoticeIds, noticeId].includes(notice.id) && !isDismissedToday(notice.id)
      )
      if (unreadNotices.length > 0) {
        const sorted = [...unreadNotices].sort((a, b) => (b.priority || 0) - (a.priority || 0))
        setPopupNotice(sorted[0])
      }
    }
  }

  const handleViewPopupNoticeDetail = (noticeId) => {
    const newReadIds = readNoticeIds.includes(noticeId) ? readNoticeIds : [...readNoticeIds, noticeId]
    setReadNoticeIds(newReadIds)
    localStorage.setItem('systemNoticeReadIds', JSON.stringify(newReadIds))
    setPopupNotice(null)
    navigate(`/ops/system-notices/${noticeId}`)
  }

  const isOperator = hasAnyRole && hasAnyRole([Roles.OPERATOR])
  const location = useLocation()
  const billingAccessAllowed = isBillingAccessAllowedPath(location.pathname)
  const billingAccessFetchEnabled = Boolean(user && !isOperator)
  const billingAccessQuery = useQuery({
    queryKey: ['billing-me-shell-access', user?.id],
    queryFn: async () => {
      const res = await api.get('/billing/me', { __silent: true, __suppressErrorPageRedirect: true })
      return res?.data?.data ?? null
    },
    enabled: billingAccessFetchEnabled,
    staleTime: 0,
    refetchOnMount: 'always',
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * (attemptIndex + 1), 3000),
  })
  const billingAccessFailed = billingAccessFetchEnabled && !billingAccessAllowed && billingAccessQuery.isError
  const billingAccessBlocked = billingAccessFetchEnabled && billingAccessQuery.data?.billingReady === false
  const billingAccessChecking = billingAccessFetchEnabled
    && !billingAccessAllowed
    && (billingAccessQuery.isLoading || (billingAccessQuery.isFetching && billingAccessQuery.data?.billingReady === false))
  const billingStatusPath = ENABLE_USER_BILLING ? '/billing' : null
  const layoutTier = resolveShellLayoutTier(location.pathname)
  const mainContentClass = shellMainContentClass(layoutTier)

  React.useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname])

  React.useEffect(() => {
    if (!menuOpen) return undefined
    const mq = window.matchMedia('(max-width: 1279px)')
    if (!mq.matches) return undefined
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [menuOpen])

  return (
    <div className="sgez-app-shell min-h-screen bg-white text-slate-900">
      <div className="flex min-h-screen">
        <ShellSidebar
          user={user}
          hasAnyRole={hasAnyRole}
          hasAnyPermission={hasAnyPermission}
          hasAnyCapability={hasAnyCapability}
          serviceLogoSrc={shellBrandedNav ? shellNavLogoSrc : logo}
          serviceLogoAlt={shellBrandedNav ? (user?.orgName || '설계 EZ') : '설계 EZ'}
          onLogout={() => { logout(); navigate('/login') }}
          mobileOpen={menuOpen}
          onMobileClose={() => setMenuOpen(false)}
          billingAccessBlocked={billingAccessBlocked}
        />
        <div className="flex min-w-0 flex-1 flex-col bg-white">
          <header className="sticky top-0 z-30 border-b bg-white">
            <div className="flex min-w-0 items-center justify-between gap-3 px-4 py-3 md:px-6">
              <button
                type="button"
                className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded border text-slate-700 hover:bg-slate-50 xl:hidden"
                aria-label={menuOpen ? '메뉴 닫기' : '메뉴 열기'}
                aria-controls="shell-sidebar"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((v) => !v)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5">
                  <path fillRule="evenodd" d="M3.75 6.75a.75.75 0 0 1 .75-.75h15a.75.75 0 0 1 0 1.5h-15a.75.75 0 0 1-.75-.75Zm0 5.25a.75.75 0 0 1 .75-.75h15a.75.75 0 0 1 0 1.5h-15a.75.75 0 0 1-.75-.75Zm0 5.25a.75.75 0 0 1 .75-.75h15a.75.75 0 0 1 0 1.5h-15a.75.75 0 0 1-.75-.75Z" clipRule="evenodd" />
                </svg>
              </button>
              <HeaderMenuFavorites />
              <div className="relative flex min-w-0 flex-1 items-center justify-end gap-1.5 text-sm sm:gap-2 xl:flex-none xl:gap-3">
                {isOperator && (
                  <Link
                    to={SUPPORT_GUIDE_PATH}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hidden items-center rounded border border-slate-300 bg-white px-3 py-1 text-sm font-medium text-slate-800 hover:border-sky-300 hover:bg-sky-50 md:inline-flex"
                  >
                    이용가이드
                  </Link>
                )}
                <a
                  href={KAKAO_OFFICIAL_CHANNEL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden items-center rounded border border-yellow-300 bg-yellow-50 px-3 py-1 text-sm font-medium text-yellow-800 hover:bg-yellow-100 sm:inline-flex"
                >
                  채널추가
                </a>
                <Link
                  to={SUPPORT_INQUIRIES_PATH}
                  className="hidden items-center rounded border border-sky-300 bg-sky-50 px-3 py-1 text-sm font-medium text-sky-800 hover:bg-sky-100 sm:inline-flex"
                >
                  고객문의
                </Link>
                {!billingAccessBlocked && (
                  <>
                    <Link
                      to={EVENTS_PATH}
                      className="hidden items-center rounded border border-emerald-300 bg-emerald-50 px-3 py-1 text-sm font-medium text-emerald-800 hover:bg-emerald-100 sm:inline-flex"
                    >
                      이벤트
                    </Link>
                    {isOperator && (
                      <Link
                        to={VOTE_BOARD_PATH}
                        className="hidden items-center rounded border border-indigo-300 bg-indigo-50 px-3 py-1 text-sm font-medium text-indigo-800 hover:bg-indigo-100 md:inline-flex"
                      >
                        투표게시판
                      </Link>
                    )}
                  </>
                )}
            <button
              type="button"
              className="relative inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full hover:bg-slate-100 focus:outline-none"
              aria-label="알림"
              onClick={() => {
                setNotifOpen(v => {
                  const next = !v
                  if (next) markAllRead()
                  return next
                })
              }}
            >
              {badgeCount > 0 && (
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute -top-1 -right-1 inline-flex h-[18px] w-[18px] rounded-full bg-red-400 opacity-60 sgez-notify-ping"
                />
              )}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                className={`h-5 w-5 text-slate-600 ${badgeCount > 0 ? 'sgez-notify-shake' : ''}`}
              >
                <path d="M15 17h5l-1.4-1.4A2 2 0 0 1 18 14.172V11a6 6 0 0 0-12 0v3.172a2 2 0 0 1-.586 1.414L4 17h5" />
                <path d="M9 19a3 3 0 0 0 6 0" />
              </svg>
              {badgeCount > 0 && (
                <span className="absolute -top-1 -right-1 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] px-1.5 min-w-[18px] h-[18px]">
                  {badgeCount > 9 ? '9+' : badgeCount}
                </span>
              )}
            </button>
            {notifOpen && (
              <div ref={notifRef} className="absolute right-0 top-[calc(100%+8px)] w-[360px] max-w-[90vw] rounded-lg border bg-white shadow-lg">
                <div className="flex items-center justify-between px-3 py-2 border-b">
                  <div className="font-semibold text-sm">알림</div>
                  <div className="flex items-center gap-2">
                    <button type="button" className="text-xs text-slate-600 hover:underline" onClick={() => clear()}>
                      모두 지우기
                    </button>
                    <button type="button" className="text-xs text-slate-600 hover:underline" onClick={() => setNotifOpen(false)}>
                      닫기
                    </button>
                  </div>
                </div>
                <div className="max-h-80 overflow-auto">
                  {(Array.isArray(notifications) ? notifications : []).slice(0, 15).map((n) => {
                    const p = n?.payload || {}
                    const title = p?.title || '알림'
                    const summary = p?.summary || p?.message || p?.insurerOrgName || ''
                    const when = formatApiDateTimeDisplay(n?.receivedAtDisplay, n?.receivedAt)
                    const href = resolveNotificationHref(p)
                    return (
                      <div key={n.id} className="px-3 py-2 border-b last:border-b-0 hover:bg-slate-50">
                        <div className="flex items-start justify-between gap-2">
                          <button
                            type="button"
                            className="text-left flex-1"
                            onClick={() => {
                              const moved = openNotificationHref(href, navigate, null)
                              if (moved) dismiss(n.id)
                              else markRead(n.id)
                              setNotifOpen(false)
                            }}
                          >
                            <div className="text-sm font-medium text-slate-800">{title}</div>
                            {summary && <div className="text-xs text-slate-600 mt-0.5">{summary}</div>}
                            <div className="text-[11px] text-slate-400 mt-1">{when}</div>
                          </button>
                          <button type="button" className="text-xs text-slate-400 hover:text-slate-700" onClick={() => dismiss(n.id)}>
                            삭제
                          </button>
                        </div>
                      </div>
                    )
                  })}
                  {(Array.isArray(notifications) ? notifications : []).length === 0 && (
                    <div className="px-3 py-6 text-center text-sm text-slate-500">알림이 없습니다</div>
                  )}
                </div>
                <div className="px-3 py-2 border-t text-xs text-slate-500">
                  알림을 클릭하면 관련 화면으로 이동합니다.
                </div>
              </div>
            )}
              </div>
            </div>
            <BillingPaywallShellNudge />
          </header>
          <main className="flex flex-1 flex-col bg-sgez-background px-2 py-3 sm:px-4 sm:py-5 md:px-5 md:py-6">
            <div className={`sgez-app-main sgez-page-stage ${layoutTier === SHELL_LAYOUT_TIERS.FULL ? 'sgez-page-stage--full' : ''} ${mainContentClass}`}>
              {billingAccessAllowed
                ? children
                : billingAccessChecking
                  ? <BillingAccessLoadingPage />
                  : billingAccessFailed
                    ? <BillingAccessErrorPage />
                    : billingAccessBlocked
                      ? <BillingAccessRequiredPage billingStatusPath={billingStatusPath} />
                      : children}
            </div>
          </main>
          <footer className="border-t border-slate-200 bg-white">
            <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 py-2 text-xs text-slate-500 md:justify-end md:px-6">
              <Link className="hover:text-brand-sky" to="/terms">이용약관</Link>
              <Link className="hover:text-brand-sky" to="/privacy">개인정보처리방침</Link>
              <Link className="hover:text-brand-sky" to="/refund-policy">환불·취소 정책</Link>
              <span>© {new Date().getFullYear()} 설계EZ</span>
            </div>
          </footer>
        </div>
      </div>

      {/* 팝업 공지 모달 */}
      {popupNotice && (
        <DialogBackdrop>
          <DialogPanel className="max-w-2xl" aria-labelledby="sgez-system-notice-title">
            <div className="p-6">
              <div className="mb-4 flex items-center justify-between">
                <h2 id="sgez-system-notice-title" className="text-xl font-bold text-slate-900">{popupNotice.title}</h2>
                <button
                  type="button"
                  onClick={() => handleClosePopup(popupNotice.id)}
                  className="text-slate-400 hover:text-slate-600"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5">
                    <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
                  </svg>
                </button>
              </div>
              <div className="mb-6 max-h-96 overflow-y-auto">
                <SanitizedRichText html={popupNotice.content} className="system-notice-html" />
              </div>
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => handleDismissToday(popupNotice.id)}
                  className="text-sm text-slate-500 hover:text-slate-700"
                >
                  오늘 그만 보기
                </button>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => handleViewPopupNoticeDetail(popupNotice.id)}
                    className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    상세보기
                  </button>
                  <button
                    type="button"
                    onClick={() => handleClosePopup(popupNotice.id)}
                    className="rounded-lg bg-brand-sky px-4 py-2 text-sm font-semibold text-white hover:bg-brand-sky/90"
                  >
                    확인
                  </button>
                </div>
              </div>
            </div>
          </DialogPanel>
        </DialogBackdrop>
      )}
    </div>
  )
}

export default function App() {
  // 공지 외부발송(카카오/SMS) 관련 화면(템플릿/발송로그)은 현재 숨김.
  // 필요 시 `.env`에 REACT_APP_ENABLE_NOTICE_EXTERNAL_SEND=true 설정으로 노출.
  const enableNoticeExternalSend = String(process.env.REACT_APP_ENABLE_NOTICE_EXTERNAL_SEND || '').toLowerCase() === 'true'
  // 설계의뢰 공유링크(비로그인 열람) UI는 기본 비활성화
  // 필요 시 `.env`에 REACT_APP_ENABLE_REQUEST_SHARE_LINK=true 설정으로 노출.
  const enableRequestShareLink = String(process.env.REACT_APP_ENABLE_REQUEST_SHARE_LINK || '').toLowerCase() === 'true'
  // 약관 라이브러리: 기본 비활성화
  // 필요 시 `.env`에 REACT_APP_ENABLE_TERMS_LIBRARY=true 설정으로 노출.
  const enableTermsLibrary = String(process.env.REACT_APP_ENABLE_TERMS_LIBRARY || '').toLowerCase() === 'true'
  return (
    <BrowserRouter>
      <AuthProvider>
        <QueryClientProvider client={queryClient}>
          <WebPageviewCollector />
          <ToastProvider>
            <GlobalToastBridge />
            <NotificationProvider>
              <ErrorBoundary>
                <Routes>
                  <Route path="/" element={<RootRoute />} />
                  <Route
                    path="/menu-overview"
                    element={
                      <RequireAuth>
                        <Shell>
                          <MenuOverviewPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  {/* 공개: 모바일 명함/랜딩 (로그인 불필요) */}
                  <Route path="/landing/sgez" element={<SgezLandingPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/product" element={<ProductPage />} />
                  <Route path="/pricing" element={<PricingPage />} />
                  <Route path="/checkout" element={<CheckoutPage />} />
                  <Route path="/refund-policy" element={<RefundPolicyPage />} />
                  <Route path="/support/notices" element={<SupportNoticesPage />} />
                  <Route path="/support/qna" element={<SupportQnaPage />} />
                  <Route path="/support/faq" element={<SupportFaqPage />} />
                  <Route
                    path="/support/guide"
                    element={
                      <RequireAuth>
                        <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                          <SupportGuidePage />
                        </RequireRole>
                      </RequireAuth>
                    }
                  />
                  <Route path="/support" element={<SupportHubPage />} />
                  <Route path="/inquiry" element={<PublicInquiryPage />} />
                  <Route path="/b2b" element={<B2bPartnershipPage />} />
                  <Route path="/terms" element={<TermsPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/@:code" element={<PublicCardPage />} />
                  <Route path="/p/:code" element={<PublicLandingPage />} />
                  <Route path="/c/:code" element={<CategoryLandingPageViewPage />} />
                  {/* 공개: 조직 홈페이지 */}
                  <Route path="/orgs/:orgId/landing" element={<OrgLandingPage />} />
                  {/* 공개: "@code" 형태 라우팅 보강 (환경에 따라 "/@:code" 매칭이 불안정한 케이스 대응) */}
                  <Route path="/:handle" element={<PublicCardPage />} />
                  {/* 공개: 비대면 서명(로그인 불필요) — 발급 URL은 /share/… OG 프리뷰용, 브라우저는 /sign/… 로 진입 */}
                  <Route path="/share/claim-signature/:token" element={<ShareClaimSignatureRedirect />} />
                  <Route path="/share/claim-intake/:token" element={<ShareClaimIntakeRedirect />} />
                  <Route path="/sign/claim-signature/:token" element={<SignClaimSignaturePage />} />
                  <Route path="/sign/after-contract-certificate/:token" element={<SignAfterContractCertificatePage />} />
                  {/* 공개: 고객 청구 입력 링크 (메뉴 미노출) */}
                  <Route path="/claims/intake/:token" element={<ClaimIntakePublicPage />} />
                  {/* 공개: 고객 직접 인증 정보 입력 (메뉴 미노출) */}
                  <Route path="/health-auth/:token" element={<HealthAuthPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/unauthorized" element={<UnauthorizedPage />} />
                  <Route path="/forbidden" element={<ForbiddenPage />} />
                  <Route path="/bad-request" element={<BadRequestPage />} />
                  <Route path="/internal-server-error" element={<InternalServerErrorPage />} />
                  <Route path="/bad-gateway" element={<BadGatewayPage />} />
                  <Route path="/service-unavailable" element={<ServiceUnavailablePage />} />
                  <Route path="/gateway-timeout" element={<GatewayTimeoutPage />} />
                  <Route path="/signup" element={<SignupPage />} />
                  <Route path="/auth/bridge" element={<AuthBridgePage />} />
                  <Route path="/auth/naver/callback" element={<NaverCallbackPage />} />
                  <Route path="/find-id" element={<FindIdPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  {/* 비로그인 공개 공유 링크(기본 숨김) */}
                  {enableRequestShareLink && (
                    <Route path="/share/request/:token" element={<RequestSharePage />} />
                  )}
                  {/* <Route path="/invites/accept/:code" element={<InviteAcceptPage />} /> */}
                  {/* <Route path="/invites" element={<RequireAuth><Shell><RequirePermission permissions={[Permissions.USER_INVITE, Permissions.ORG_MANAGE]} fallback={<ForbiddenPage />}><InvitesPage /></RequirePermission></Shell></RequireAuth>} /> */}
                  <Route
                    path="/orgs"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequirePermission permissions={[Permissions.ORG_MANAGE]} fallback={<ForbiddenPage />}>
                            <RequireVisibleMenuKey menuKey="MENU_ORGS" fallback={<ForbiddenPage />}>
                              <OrgsPage />
                            </RequireVisibleMenuKey>
                          </RequirePermission>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/orgs/:orgId/landing/edit"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequirePermission permissions={[Permissions.ORG_MANAGE]} fallback={<ForbiddenPage />}>
                            <OrgLandingPageEditPage />
                          </RequirePermission>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/users"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequirePermission permissions={[Permissions.USER_INVITE, Permissions.ORG_MANAGE]} fallback={<ForbiddenPage />}>
                            <RequireVisibleMenuKey menuKey="MENU_USERS" fallback={<ForbiddenPage />}>
                              <UsersPage />
                            </RequireVisibleMenuKey>
                          </RequirePermission>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/signups"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequirePermission permissions={[Permissions.USER_INVITE, Permissions.ORG_MANAGE]} fallback={<ForbiddenPage />}>
                            <SignupRequestsPage />
                          </RequirePermission>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/signups/:id"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequirePermission permissions={[Permissions.USER_INVITE, Permissions.ORG_MANAGE]} fallback={<ForbiddenPage />}>
                            <SignupRequestDetailPage />
                          </RequirePermission>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/withdrawal-approvals"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <WithdrawalApprovalRequestsPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/availability"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequirePermission permissions={[Permissions.USER_INVITE, Permissions.ORG_MANAGE]} fallback={<ForbiddenPage />}>
                            <AvailabilityAdminPage />
                          </RequirePermission>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/users/:id"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequirePermission permissions={[Permissions.USER_INVITE, Permissions.ORG_MANAGE]} fallback={<ForbiddenPage />}>
                            <RequireVisibleMenuKey menuKey="MENU_USERS" fallback={<ForbiddenPage />}>
                              <UserDetailPage />
                            </RequireVisibleMenuKey>
                          </RequirePermission>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/profile"
                    element={
                      <RequireAuth>
                        <Shell>
                          <ProfilePage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/sales/public-card"
                    element={
                      <RequireAuth>
                        <Shell>
                          <MyPublicCardPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/sales/public-landing"
                    element={
                      <RequireAuth>
                        <Shell>
                          <MyPublicLandingPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/sales/org-homepage"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireVisibleMenuKey menuKey="MENU_SALES_ORG_HOMEPAGE" fallback={<ForbiddenPage />}>
                            <SalesOrgHomepagePage />
                          </RequireVisibleMenuKey>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/public/counsel-surveys/t/:token"
                    element={<PublicCounselSurveyPage />}
                  />
                  <Route
                    path="/work/surveys"
                    element={<Navigate to="/support/surveys" replace />}
                  />
                  <Route
                    path="/support/surveys"
                    element={
                      <RequireAuth>
                        <Shell>
                          <SurveyManagementPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/support/surveys/edit/:categoryCode"
                    element={
                      <RequireAuth>
                        <Shell>
                          <SurveyCatalogTemplateEditPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/support/surveys/create"
                    element={
                      <RequireAuth>
                        <Shell>
                          <SurveyCreationPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/support/surveys/my/:id/edit"
                    element={
                      <RequireAuth>
                        <Shell>
                          <SurveyMyTemplateEditPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/support/surveys/sessions/:id/fill"
                    element={
                      <RequireAuth>
                        <Shell>
                          <CounselSurveySessionRunPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/support/customers/work-queue"
                    element={
                      <RequireAuth>
                        <Shell>
                          <CustomerWorkQueuePage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/support/customer-memos"
                    element={
                      <RequireAuth>
                        <Shell>
                          <CustomerMemoSearchPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/app/customer-memos/:id/editor"
                    element={
                      <RequireAuth>
                        <CustomerMemoAppEditorPage />
                      </RequireAuth>
                    }
                  />                  <Route
                    path="/support/customers/:id/coverage-remodeling-simulation"
                    element={
                      <RequireAuth>
                        <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                          <CoverageRemodelingSimulationPage />
                        </RequireRole>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/support/customers/:id"
                    element={
                      <RequireAuth>
                        <Shell>
                          <CustomerDetailPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/support/customers"
                    element={
                      <RequireAuth>
                        <Shell>
                          <CustomerManagementPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/support/maturity-ledger"
                    element={
                      <RequireAuth>
                        <Shell>
                          <CustomerMaturityLedgerPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/support/customer-tools"
                    element={
                      <RequireAuth>
                        <Shell>
                          <MedicalExpenseReimbursementCalculatorPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/support/customer-tools/medical-expense-reimbursement"
                    element={<Navigate to="/support/customer-tools" replace />}
                  />
                  <Route
                    path="/support/customer-registration"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <CustomerRegistrationWizardPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/category-landing-pages"
                    element={
                      <RequireAuth>
                        <Shell>
                          <CategoryLandingPagesPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/category-landing-pages/:id/edit"
                    element={
                      <RequireAuth>
                        <Shell>
                          <CategoryLandingPageEditPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/category-landing-pages/:id/preview"
                    element={
                      <RequireAuth>
                        <CategoryLandingPagePreview />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/sales/consultations"
                    element={
                      <RequireAuth>
                        <Shell>
                          <MyConsultationsPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/fire/building-registry"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireHealthAnalysisAccess fallback={<ForbiddenPage />}>
                            <FireBuildingRegistryPage />
                          </RequireHealthAnalysisAccess>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/requests"
                    element={
                      <RequireAuth>
                        <Shell>
                            <RequestsPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/requests/new"
                    element={
                      <RequireAuth>
                        <Shell>
                            <RequestNewPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/requests/:id"
                    element={
                      <RequireAuth>
                        <Shell>
                            <RequestDetailPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/notices"
                    element={
                      <RequireAuth>
                        <Shell>
                          <NoticesPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/notices/:id"
                    element={
                      <RequireAuth>
                        <Shell>
                          <NoticeDetailPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/notices/:id/edit"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequirePermission permissions={[Permissions.NOTICE_CREATE, Permissions.NOTICE_APPROVE, Permissions.NOTICE_SEND]} fallback={<ForbiddenPage />}>
                            <NoticeEditPage />
                          </RequirePermission>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/notices/templates"
                    element={
                      <RequireAuth>
                        <Shell>
                          {enableNoticeExternalSend ? (
                            <RequirePermission permissions={[Permissions.TEMPLATE_MANAGE]} fallback={<ForbiddenPage />}>
                              <NoticeTemplatesPage />
                            </RequirePermission>
                          ) : (
                            <NotFoundPage />
                          )}
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/templates/homepage"
                    element={<Navigate to="/ops/templates/homepage/org" replace />}
                  />
                  <Route
                    path="/ops/templates/homepage/:tab"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequirePermission permissions={[Permissions.TEMPLATE_MANAGE]} fallback={<ForbiddenPage />}>
                            <HomepageTemplatesPage />
                          </RequirePermission>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/templates/org-homepage"
                    element={<Navigate to="/ops/templates/homepage/org" replace />}
                  />
                  <Route
                    path="/ops/templates/org-homepage/assets"
                    element={<Navigate to="/ops/templates/homepage/org-assets" replace />}
                  />
                  <Route
                    path="/ops/templates/user-homepage"
                    element={<Navigate to="/ops/templates/homepage/user" replace />}
                  />
                  <Route
                    path="/ops/templates/after-contract-certificates"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <AfterContractCertificateTemplatesPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/templates/policy-delivery-books"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <PolicyDeliveryBookTemplatesPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/templates/coverage-rules"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <CoverageRuleTemplatesPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/templates/reports"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <ReportTemplatesPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/templates/category-landing"
                    element={<Navigate to="/ops/templates/homepage/category" replace />}
                  />
                  <Route
                    path="/ops/templates/survey"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <SurveyTemplatesPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/templates/messages"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequirePermission permissions={[Permissions.TEMPLATE_MANAGE]} fallback={<ForbiddenPage />}>
                            <MessageTemplatesPage />
                          </RequirePermission>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/templates/customer-memos"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <CustomerMemoTemplatesPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/templates/alimtalk"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <OperatorNotificationTemplatesPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/forms/claims"
                    element={<Navigate to="/ops/forms/claims/templates" replace />}
                  />
                  <Route
                    path="/ops/forms/claims/:tab"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <ClaimFormsManagementPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/stats"
                    element={<Navigate to="/ops/stats/members" replace />}
                  />
                  <Route
                    path="/ops/stats/:tab"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <OpsStatsPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/notices/logs"
                    element={
                      <RequireAuth>
                        <Shell>
                          {enableNoticeExternalSend ? (
                            <RequirePermission permissions={[Permissions.NOTICE_LOG_VIEW]} fallback={<ForbiddenPage />}>
                              <NoticeLogsPage />
                            </RequirePermission>
                          ) : (
                            <NotFoundPage />
                          )}
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/notices/new"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequirePermission permissions={[Permissions.NOTICE_CREATE, Permissions.NOTICE_APPROVE, Permissions.NOTICE_SEND]} fallback={<ForbiddenPage />}>
                            <NoticeNewPage />
                          </RequirePermission>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/counterparts"
                    element={
                      <RequireAuth>
                        <Shell>
                          <CounterpartDirectoryPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/billing"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireCapability capabilities={[Capabilities.BILLING]} fallback={<ForbiddenPage />}>
                            {ENABLE_USER_BILLING ? <UserBillingPage /> : <Navigate to="/profile" replace />}
                          </RequireCapability>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/billing/subscribe"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireCapability capabilities={[Capabilities.BILLING]} fallback={<ForbiddenPage />}>
                            <BillingSubscribePage />
                          </RequireCapability>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/billing/orders"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireCapability capabilities={[Capabilities.BILLING]} fallback={<ForbiddenPage />}>
                            {ENABLE_USER_BILLING ? <UserBillingPage /> : <Navigate to="/profile" replace />}
                          </RequireCapability>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/billing/refunds"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireCapability capabilities={[Capabilities.BILLING]} fallback={<ForbiddenPage />}>
                            {ENABLE_USER_BILLING ? <UserBillingPage /> : <Navigate to="/profile" replace />}
                          </RequireCapability>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/billing/documents"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireCapability capabilities={[Capabilities.BILLING]} fallback={<ForbiddenPage />}>
                            {ENABLE_USER_BILLING ? <UserBillingPage /> : <Navigate to="/profile" replace />}
                          </RequireCapability>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/billing/org"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireCapability capabilities={[Capabilities.BILLING]} fallback={<ForbiddenPage />}>
                            {ENABLE_USER_BILLING ? <UserBillingPage /> : <Navigate to="/profile" replace />}
                          </RequireCapability>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/billing/org/seats"
                    element={<Navigate to="/billing/org" replace />}
                  />
                  <Route
                    path="/subscriptions/config"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <BillingConfigPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/subscriptions/orgs/*"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <BillingOrgManagementPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/subscriptions/statistics"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <BillingStatisticsPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/subscriptions/dashboard"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <SubscriptionsPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/subscriptions/hub/status"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <SubscriptionsPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/subscriptions/hub/operation-queue"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <SubscriptionsPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/subscriptions/hub/orders"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <SubscriptionsPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  {ENABLE_BILLING_COUPONS && (
                    <Route
                      path="/subscriptions/hub/coupons"
                      element={
                        <RequireAuth>
                          <Shell>
                            <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                              <SubscriptionsPage />
                            </RequireRole>
                          </Shell>
                        </RequireAuth>
                      }
                    />
                  )}
                  {ENABLE_BILLING_POINTS && (
                    <Route
                      path="/subscriptions/hub/points"
                      element={
                        <RequireAuth>
                          <Shell>
                            <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                              <SubscriptionsPage />
                            </RequireRole>
                          </Shell>
                        </RequireAuth>
                      }
                    />
                  )}
                  <Route
                    path="/subscriptions/hub/refunds"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <SubscriptionsPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/subscriptions/hub/service-usage"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <SubscriptionsPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/subscriptions/hub/audit-logs"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <SubscriptionsPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/subscriptions/hub/billing-logs"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <SubscriptionsPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route path="/subscriptions/hub" element={<Navigate to="/subscriptions/hub/status" replace />} />
                  <Route path="/subscriptions/status" element={<Navigate to="/subscriptions/hub/status" replace />} />
                  <Route path="/subscriptions/operation-queue" element={<Navigate to="/subscriptions/hub/operation-queue" replace />} />
                  <Route path="/subscriptions/orders" element={<Navigate to="/subscriptions/hub/orders" replace />} />
                  <Route path="/subscriptions/refunds" element={<Navigate to="/subscriptions/hub/refunds" replace />} />
                  <Route path="/subscriptions/service-usage" element={<Navigate to="/subscriptions/hub/service-usage" replace />} />
                  <Route path="/subscriptions/billing-logs" element={<Navigate to="/subscriptions/hub/billing-logs" replace />} />
                  <Route path="/subscriptions/audit-logs" element={<Navigate to="/subscriptions/hub/audit-logs" replace />} />
                  {ENABLE_BILLING_COUPONS && (
                    <Route path="/subscriptions/coupons" element={<Navigate to="/subscriptions/hub/coupons" replace />} />
                  )}
                  {!ENABLE_BILLING_COUPONS && (
                    <Route path="/subscriptions/coupons" element={<Navigate to="/subscriptions/hub/status" replace />} />
                  )}
                  {ENABLE_BILLING_POINTS && (
                    <Route path="/subscriptions/points" element={<Navigate to="/subscriptions/hub/points" replace />} />
                  )}
                  {!ENABLE_BILLING_POINTS && (
                    <Route path="/subscriptions/points" element={<Navigate to="/subscriptions/hub/status" replace />} />
                  )}
                  <Route path="/subscriptions" element={<Navigate to="/subscriptions/dashboard" replace />} />
                  <Route
                    path="/sales-images"
                    element={
                      <RequireAuth>
                        <Shell>
                          <SalesImagesListPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/sales-images/org-templates"
                    element={
                      <RequireAuth>
                        <Shell>
                          <SalesImagesOrgTemplatesPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/sales-images/new"
                    element={
                      <RequireAuth>
                        <Shell>
                          <SalesImagesCreatePage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/sales-images/:id/edit"
                    element={
                      <RequireAuth>
                        <Shell>
                          <SalesImagesEditPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/sales/uw3nn/exceptions"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireHealthAnalysisAccess fallback={<ForbiddenPage />}>
                            <Uw3nnExceptionsLookupPage />
                          </RequireHealthAnalysisAccess>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/data/uw3nn/exception-rules"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <Uw3nnExceptionRulesAdminPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/data/insurers"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <InsurersMasterAdminPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/data/insurers/:id"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <InsurersMasterAdminPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/data/permissions"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <PermissionsAdminPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/data/raw"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <RawDataAdminPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/data/public-data"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <PublicDataAdminPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/data/terms"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <OpsTermsManagementPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/data/design-support-reference"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <DesignSupportDataAdminPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/files"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <FilesPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/alert-management"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <Navigate to="/ops/other-management/alerts" replace />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/push-notifications"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <Navigate to="/ops/other-management/push-notifications" replace />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/alimtalk-sender"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <OperatorAlimtalkSenderPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/other-management"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <OpsOtherManagementPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/other-management/:section"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <OpsOtherManagementPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/counsel-practice-management"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <OpsCounselPracticeManagementPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/counsel-practice-management/:section"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <OpsCounselPracticeManagementPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/customer-work-hub"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <OpsCustomerWorkHubManagementPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/fonts"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <Navigate to="/ops/other-management/fonts" replace />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/external-program-versions"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <Navigate to="/ops/other-management/versions" replace />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/kakao-talk-management"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <OpsKakaoTalkManagementPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/legal-policies"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <OpsLegalPolicyManagementPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/support-guide/manuals"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <OpsSupportGuideManualsPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/operation-tests"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <OpsApiTestPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/api-test"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <OpsApiTestPage initialMainTab="api" />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/history-management"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <OpsHistoryManagementPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/usage-dashboard"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <OpsUsageDashboardPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/org-user-management"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <OpsOrganizationUserManagementPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/operational-costs"
                    element={                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <OpsOperationalCostsPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/audit-logs"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <AuditLogsPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/system-notices"
                    element={
                      <RequireAuth>
                        <Shell>
                          {/* 모든 로그인한 유저가 읽기 가능 (작성/수정은 OPERATOR만 가능) */}
                          <SystemNoticesPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/system-notices/new"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <SystemNoticeNewPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/system-notices/:id"
                    element={
                      <RequireAuth>
                        <Shell>
                          <SystemNoticeDetailPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/system-notices/:id/edit"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <SystemNoticeEditPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/banners"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <Navigate to="/ops/other-management/banners" replace />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/banners/new"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <BannerNewPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/banners/:id/edit"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <BannerEditPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/org-banners"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequirePermission permissions={[Permissions.ORG_BANNER_MANAGE, Permissions.ORG_MANAGE]} fallback={<ForbiddenPage />}>
                            <RequireVisibleMenuKey menuKey="MENU_ORG_BANNERS" fallback={<ForbiddenPage />}>
                              <OrgBannersPage />
                            </RequireVisibleMenuKey>
                          </RequirePermission>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/org-banners/new"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequirePermission permissions={[Permissions.ORG_BANNER_MANAGE, Permissions.ORG_MANAGE]} fallback={<ForbiddenPage />}>
                            <RequireVisibleMenuKey menuKey="MENU_ORG_BANNERS" fallback={<ForbiddenPage />}>
                              <OrgBannerNewPage />
                            </RequireVisibleMenuKey>
                          </RequirePermission>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/org-banners/:id/edit"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequirePermission permissions={[Permissions.ORG_BANNER_MANAGE, Permissions.ORG_MANAGE]} fallback={<ForbiddenPage />}>
                            <RequireVisibleMenuKey menuKey="MENU_ORG_BANNERS" fallback={<ForbiddenPage />}>
                              <OrgBannerEditPage />
                            </RequireVisibleMenuKey>
                          </RequirePermission>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  {enableTermsLibrary && (
                    <Route
                      path="/terms-library"
                      element={
                        <RequireAuth>
                          <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                              <TermsLibraryPage />
                            </RequireRole>
                          </Shell>
                        </RequireAuth>
                      }
                    />
                  )}
                  <Route
                    path="/health-analysis"
                    element={
                      <RequireAuth>
                        <Shell>
                          <HealthAnalysisPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/hidden-insurance-money"
                    element={
                      <RequireAuth>
                        <Shell>
                          <HiddenInsuranceMoneyPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/hidden-insurance-money-landing"
                    element={
                      <RequireAuth>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <HiddenInsuranceMoneyLandingPage />
                          </RequireRole>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/health/disease-stats"
                    element={<Navigate to="/work/insurance-market-insights?tab=disease-medical-fee" replace />}
                  />
                  <Route
                    path="/design"
                    element={
                      <RequireAuth>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <DesignSupportHubPage />
                          </RequireRole>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/design/disease-code"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <DesignSupportDiseaseCodePage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/design/surgery-search"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <DesignSupportSurgerySearchPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/design/major-disease-n"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <DesignSupportMajorDiseaseNPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/org-dashboard"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequirePermission permissions={[Permissions.ORG_MANAGE]} fallback={<ForbiddenPage />}>
                            <RequireVisibleMenuKey menuKey="MENU_ORG_DASHBOARD" fallback={<ForbiddenPage />}>
                              <OrgDashboardPage />
                            </RequireVisibleMenuKey>
                          </RequirePermission>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/org-library"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <RequireVisibleMenuKey menuKey="MENU_ORG_LIBRARY" fallback={<ForbiddenPage />}>
                              <OrgLibraryPage />
                            </RequireVisibleMenuKey>
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/reports"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequirePermission permissions={[Permissions.REPORT_VIEW]} fallback={<ForbiddenPage />}>
                            <ReportsPage />
                          </RequirePermission>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/customers"
                    element={
                      <RequireAuth>
                        <Shell>
                          <CustomersPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/support/kakao-bulk-sender"
                    element={
                      <RequireAuth>
                        <Shell>
                          <KakaoBulkSenderPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/support/claims"
                    element={
                      <RequireAuth>
                        <Shell>
                          <ClaimsPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/support/after-contract-certificates"
                    element={<LegacyAfterContractCertificateRedirect />}
                  />
                  <Route
                    path="/support/claims/applications"
                    element={
                      <RequireAuth>
                        <Shell>
                          <ClaimApplicationsPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/support/claims/new"
                    element={<Navigate to="/support/claims" replace />}
                  />
                  <Route
                    path="/support/claims/templates"
                    element={<Navigate to="/ops/forms/claims/templates" replace />}
                  />
                  <Route
                    path="/support/claims/:id"
                    element={
                      <RequireAuth>
                        <Shell>
                          <ClaimApplicationDetailPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/ops/claims/templates"
                    element={<Navigate to="/ops/forms/claims/templates" replace />}
                  />
                  <Route
                    path="/ops/claims/insurer-fax"
                    element={<Navigate to="/ops/forms/claims/fax" replace />}
                  />
                  <Route
                    path="/ops/claims/stats"
                    element={<Navigate to="/ops/stats/claims" replace />}
                  />
                  <Route
                    path="/work/insurance-market-insights"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <InsuranceMarketInsightsPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/work/insurance-news"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireInsuranceNewsAccess fallback={<ForbiddenPage />}>
                            <InsuranceNewsPage />
                          </RequireInsuranceNewsAccess>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/work/insurance-news/new"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <InsuranceNewsNewPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/work/insurance-news/:id/edit"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <InsuranceNewsEditPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/work/insurance-news/daily-digest/new"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <InsuranceNewsDailyDigestNewPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/work/insurance-news/daily-digest/:id/edit"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <InsuranceNewsDailyDigestEditPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/work/insurer-newsletters"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireInsuranceNewsAccess fallback={<ForbiddenPage />}>
                            <InsurerNewslettersPage />
                          </RequireInsuranceNewsAccess>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/work/insurer-business-hub"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <InsurerBusinessHubPage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/work/insurer-systems"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireHealthAnalysisAccess fallback={<ForbiddenPage />}>
                            <InsurerSystemsPage />
                          </RequireHealthAnalysisAccess>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/work/personal-tasks"
                    element={
                      <RequireAuth>
                        <Shell>
                          <PersonalTaskBoardPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/work/calendar"
                    element={
                      <RequireAuth>
                        <Shell>
                          <CalendarPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/work/policy-lookup"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireHealthAnalysisAccess fallback={<ForbiddenPage />}>
                            <PolicyLookupPage />
                          </RequireHealthAnalysisAccess>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/work/education"
                    element={
                      <RequireAuth>
                        <Shell>
                          <EducationCenterPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/work/counsel-practice"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireRole roles={[Roles.OPERATOR]} fallback={<ForbiddenPage />}>
                            <CounselPracticePage />
                          </RequireRole>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/work/tools"
                    element={
                      <RequireAuth>
                        <Shell>
                          <RequireHealthAnalysisAccess fallback={<ForbiddenPage />}>
                            <WorkSupportToolsPage />
                          </RequireHealthAnalysisAccess>
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/events"
                    element={
                      <RequireAuth>
                        <Shell>
                          <EventsPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/events/new"
                    element={
                      <RequireAuth>
                        <Shell>
                          <EventsPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/events/:id/edit"
                    element={
                      <RequireAuth>
                        <Shell>
                          <EventsPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/events/:id"
                    element={
                      <RequireAuth>
                        <Shell>
                          <EventsPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/vote-board"
                    element={
                      <RequireAuth>
                        <Shell>
                          <VoteBoardPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/vote-board/new"
                    element={
                      <RequireAuth>
                        <Shell>
                          <VoteBoardPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/vote-board/:id/edit"
                    element={
                      <RequireAuth>
                        <Shell>
                          <VoteBoardPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/vote-board/:id"
                    element={
                      <RequireAuth>
                        <Shell>
                          <VoteBoardPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/support/inquiries"
                    element={
                      <RequireAuth>
                        <Shell>
                          <SupportInquiriesPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/support/inquiries/:id"
                    element={
                      <RequireAuth>
                        <Shell>
                          <SupportInquiryDetailPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/support/account-deletion"
                    element={
                      <RequireAuth>
                        <Shell>
                          <AccountDeletionPage />
                        </Shell>
                      </RequireAuth>
                    }
                  />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </ErrorBoundary>
            </NotificationProvider>
          </ToastProvider>
        </QueryClientProvider>
      </AuthProvider>
    </BrowserRouter>
  )
}
